import { Controller, useFormContext } from "react-hook-form";
import { Domains, Languages, FieldSelectOption } from "@/core/types/types";
import { SelectChangeEvent } from "@mui/material/Select";
import { useEffect, useState } from "react";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { camundaApi } from "@/core/api/camundaApi";
import { getLanguageByDomain, getTopLevelDomain } from "@/core/utils";
import { FieldProps } from "@/core/components/Field/index";
import { SelectField as ReusableSelectField } from "@flightright/reusable-components";
import { HOTJAR_ATTRIBUTES as extraProps } from "@/core/data";
import { useFieldError } from "@/core/hooks/useFieldError/useFieldError";
import { countriesApi } from "@/core/api/countriesApi";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";
import { parseDefaultValue } from "@/core/utils/parseDefaultValue";

const NOT_FOUND_OPTION: FieldSelectOption[] = [
  {
    value: "null",
    label: "No options / No option source was provided",
  },
];

interface SelectFieldProps {
  fieldProps: FieldProps;
  initialValue?: string;
  options: FieldSelectOption[];
  isEditable?: boolean;
}

const SelectField = ({
  fieldProps: { field },
  initialValue,
  options,
}: SelectFieldProps) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    initialValue
  );
  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useFormContext();
  const translatedOptions: FieldSelectOption[] | undefined = options?.map(
    (item) => {
      item.label = t(item.label);
      return item;
    }
  );
  const { errorMessage, errorTranslationKey } = useFieldError(errors, field);

  useEffect(() => {
    if (initialValue) {
      setSelectedValue(initialValue);
    }
  }, [initialValue]);

  const handleChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event;
    setSelectedValue(value as string);
    setValue(field.key, value);
    clearErrors(field.key);
  };

  return (
    <Controller
      name={field.key}
      control={control}
      rules={{
        required: {
          value: field.validate?.required || false,
          message: t(`validate.${errorTranslationKey}.required.message`),
        },
      }}
      render={() => (
        <ReusableSelectField
          id={field.key}
          label={t(field.label)}
          disabled={field.disabled}
          options={translatedOptions}
          value={selectedValue ?? ""}
          handleChange={handleChange}
          error={errorMessage}
          {...extraProps}
        ></ReusableSelectField>
      )}
    />
  );
};

const SelectFieldWithCamunda = (fieldProps: FieldProps) => {
  const { setValue } = useFormContext();
  const [value, setSelectValue] = useState<string>("");
  const [options, setOptions] = useState<FieldSelectOption[]>(NOT_FOUND_OPTION);

  const { field } = fieldProps;
  const { defaultValue: defaultValueConfig } = field;

  const parsedDefaultValue = parseDefaultValue(defaultValueConfig);
  const defaultValue =
    useDefaultValue(parsedDefaultValue?.variableName) ??
    parsedDefaultValue?.value;

  const getCountries = async () => {
    const tld: Domains | string = getTopLevelDomain();
    let language: Languages = getLanguageByDomain(tld as Domains);

    try {
      const { countries } = await camundaApi.getCountries(language);
      setOptions(countries);
    } catch (error) {
      const { countries } = await countriesApi.getCountries();
      setOptions(countries);
    } finally {
      if (!options.length) {
        setOptions(NOT_FOUND_OPTION);
      }
    }
  };

  useEffect(() => {
    const { values, valuesKey } = field;
    let options: FieldSelectOption[] = values ? values : NOT_FOUND_OPTION;

    if (valuesKey) {
      getCountries();
    } else {
      setOptions(options);
    }
  }, []);

  useEffect(() => {
    if (defaultValue) {
      const existInSelect = options.find(
        (option) => option.value === defaultValue
      );
      if (existInSelect) {
        setSelectValue(defaultValue);
        setValue(field.key, defaultValue);
      }
    }
  }, [options, defaultValue]);

  return (
    <SelectField
      fieldProps={fieldProps}
      initialValue={value}
      options={options}
    />
  );
};

export { SelectField, SelectFieldWithCamunda };
